import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Label } from "@/components/ui/input";
import { MultiCombobox } from "@/components/ui/multi-combobox";
import { GET_EXTERNAL_DATA_REQUEST } from "@/redux/actions";

// Dormakaba-Türberechtigungstypen: RZ = Raumzone, DG = Türgruppe, DR = Tür
const DOOR_TYPES = [
  { type: "RZ", label: "Raumzonen", placeholder: "Raumzonen auswählen" },
  { type: "DG", label: "Türgruppen", placeholder: "Türgruppen auswählen" },
  { type: "DR", label: "Türen", placeholder: "Türen auswählen" },
];

/**
 * useDoorPermissions — lädt die Dormakaba-Türdaten eines Standorts und stellt
 * Optionen je Typ bereit. Gleiche Bauart wie im Web-Frontend, nur an den Store
 * des Add-ins angeschlossen.
 */
export function useDoorPermissions(locationId) {
  const dispatch = useDispatch();
  const externalData = useSelector((state) => state.company.data.external_data);
  const permissions = useSelector((state) => state.user.data?.permissions) || [];

  const canManagePermissions = permissions.some((permission) =>
    ["Admin", "Visitor.Admin", "Visitor.Manager"].includes(
      permission.role_name || permission,
    ),
  );

  useEffect(() => {
    if (!locationId) return;
    dispatch(
      GET_EXTERNAL_DATA_REQUEST({ location_id: locationId, key: "visitor_door" }),
    );
  }, [dispatch, locationId]);

  const dormakabaData = useMemo(() => {
    if (Array.isArray(externalData)) {
      return externalData.find(
        (i) => i.source === "dormakaba_api" && i.key === "visitor_door",
      );
    }
    if (
      externalData?.source === "dormakaba_api" &&
      externalData?.key === "visitor_door"
    ) {
      return externalData;
    }
    return null;
  }, [externalData]);

  const isDormakabaActive = (dormakabaData?.data?.length || 0) > 0;

  const optionsByType = useMemo(() => {
    const build = (type) =>
      (dormakabaData?.data || [])
        .filter((i) => i.Type === type)
        .map((i) => ({ value: i.ExosID, label: i.Name, original: i }));
    return { RZ: build("RZ"), DG: build("DG"), DR: build("DR") };
  }, [dormakabaData]);

  return { isDormakabaActive, canManagePermissions, optionsByType };
}

/**
 * DoorPermissionFields — Raumzonen, Türgruppen und Türen eines Besuchers.
 * `value` ist das `door_permissions`-Array (`{ExosID, Type, Name}`). Typen ohne
 * Einträge werden weggelassen, damit im schmalen Panel nichts leer dasteht.
 */
export function DoorPermissionFields({ value, optionsByType, disabled = false, onChange }) {
  const list = Array.isArray(value) ? value : [];
  const selectedFor = (type) =>
    list.filter((p) => p?.Type === type).map((p) => p.ExosID);
  const setFor = (type, vals) => {
    const others = list.filter((p) => p?.Type !== type);
    const selected = vals
      .map((v) => optionsByType[type].find((o) => o.value === v)?.original)
      .filter(Boolean);
    onChange([...others, ...selected]);
  };

  return (
    <div className="flex flex-col gap-[0.8rem]">
      {DOOR_TYPES.map(({ type, label, placeholder }) => {
        if (!optionsByType[type]?.length) return null;
        return (
          <div key={type}>
            <Label>{label}</Label>
            <MultiCombobox
              value={selectedFor(type)}
              options={optionsByType[type]}
              disabled={disabled}
              placeholder={placeholder}
              emptyMessage={`Keine ${label} vorhanden`}
              onChange={(vals) => setFor(type, vals)}
            />
          </div>
        );
      })}
    </div>
  );
}
